"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
const jsx_runtime_1 = require("react/jsx-runtime");
const react_1 = require("react");
const react_i18next_1 = require("react-i18next");
const utils_1 = require("../../utils");
require("./OtpStep.css");
const OtpStep = ({ sessionId, loading, error, onVerifyOtp, onNext, onResendOtp, }) => {
    const { t } = (0, react_i18next_1.useTranslation)();
    const [otp, setOtp] = (0, react_1.useState)(['', '', '', '', '', '']);
    const [verified, setVerified] = (0, react_1.useState)(false);
    const [waitingForSms, setWaitingForSms] = (0, react_1.useState)(false);
    const [hasSubmittedOtp, setHasSubmittedOtp] = (0, react_1.useState)(false);
    const [resendLoading, setResendLoading] = (0, react_1.useState)(false);
    const [resendCooldown, setResendCooldown] = (0, react_1.useState)(0);
    const [resendSuccess, setResendSuccess] = (0, react_1.useState)(false);
    const [timeRemaining, setTimeRemaining] = (0, react_1.useState)(600);
    const inputRefs = (0, react_1.useRef)([]);
    (0, react_1.useEffect)(() => {
        const otpString = otp.join('');
        if (otpString.length === 6 && !loading && !verified && !hasSubmittedOtp && !error) {
            (async () => {
                setHasSubmittedOtp(true);
                setVerified(false);
                try {
                    const success = await onVerifyOtp(sessionId, otpString);
                    if (success) {
                        setVerified(true);
                        setTimeout(() => {
                            onNext();
                        }, 2000);
                    }
                }
                catch (err) {
                    // Error verifying OTP
                }
            })();
        }
    }, [otp, loading, verified, hasSubmittedOtp, error, sessionId, onVerifyOtp, onNext]);
    (0, react_1.useEffect)(() => {
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
        const isMobile = (0, utils_1.isMobileDevice)();
        if ('OTPCredential' in window && isMobile) {
            const abortController = new AbortController();
            const requestOtp = async () => {
                try {
                    setWaitingForSms(true);
                    const credential = await navigator.credentials.get({
                        otp: { transport: ['sms'] },
                        signal: abortController.signal
                    });
                    if (credential && credential.code) {
                        const otpArray = credential.code.split('').slice(0, 6);
                        setOtp(prev => {
                            const newOtp = [...prev];
                            otpArray.forEach((digit, index) => {
                                if (index < 6) {
                                    newOtp[index] = digit;
                                }
                            });
                            return newOtp;
                        });
                        if (inputRefs.current[5]) {
                            inputRefs.current[5].focus();
                        }
                        setWaitingForSms(false);
                    }
                }
                catch (err) {
                    setWaitingForSms(false);
                }
            };
            requestOtp();
            return () => {
                abortController.abort();
            };
        }
    }, []);
    (0, react_1.useEffect)(() => {
        if (resendCooldown > 0) {
            const timer = setTimeout(() => {
                setResendCooldown(resendCooldown - 1);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, [resendCooldown]);
    (0, react_1.useEffect)(() => {
        if (resendSuccess) {
            const timer = setTimeout(() => {
                setResendSuccess(false);
            }, 3000);
            return () => clearTimeout(timer);
        }
    }, [resendSuccess]);
    (0, react_1.useEffect)(() => {
        if (timeRemaining > 0 && !verified) {
            const timer = setInterval(() => {
                setTimeRemaining(prev => {
                    if (prev <= 1) {
                        clearInterval(timer);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
            return () => clearInterval(timer);
        }
    }, [timeRemaining, verified]);
    (0, react_1.useEffect)(() => {
        if (resendSuccess) {
            setTimeRemaining(600);
        }
    }, [resendSuccess]);
    const formatTime = (seconds) => {
        const minutes = Math.floor(seconds / 60);
        const remainingSeconds = seconds % 60;
        return `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`;
    };
    const handleInputChange = (index, value) => {
        if (hasSubmittedOtp) {
            setHasSubmittedOtp(false);
        }
        if (value.length > 1) {
            const pastedOtp = value.slice(0, 6).split('');
            const newOtp = [...otp];
            pastedOtp.forEach((digit, i) => {
                if (index + i < 6) {
                    newOtp[index + i] = digit;
                }
            });
            setOtp(newOtp);
            const nextIndex = Math.min(index + pastedOtp.length, 5);
            if (inputRefs.current[nextIndex]) {
                inputRefs.current[nextIndex]?.focus();
            }
        }
        else {
            const newOtp = [...otp];
            newOtp[index] = value;
            setOtp(newOtp);
            if (value && index < 5 && inputRefs.current[index + 1]) {
                inputRefs.current[index + 1]?.focus();
            }
        }
    };
    const handleKeyDown = (index, e) => {
        if (e.key === 'Backspace') {
            if (hasSubmittedOtp) {
                setHasSubmittedOtp(false);
            }
            if (!otp[index] && index > 0) {
                inputRefs.current[index - 1]?.focus();
            }
        }
    };
    const handleClear = () => {
        setOtp(['', '', '', '', '', '']);
        setHasSubmittedOtp(false);
        if (inputRefs.current[0]) {
            inputRefs.current[0].focus();
        }
    };
    const handleResend = async (medium) => {
        if (!onResendOtp || resendLoading || resendCooldown > 0)
            return;
        setResendLoading(true);
        setResendSuccess(false);
        setOtp(['', '', '', '', '', '']);
        setHasSubmittedOtp(false);
        try {
            const success = await onResendOtp(medium);
            if (success) {
                setResendSuccess(true);
                setResendCooldown(30);
                if (inputRefs.current[0]) {
                    inputRefs.current[0].focus();
                }
            }
        }
        catch (err) {
            // Error resending OTP
        }
        finally {
            setResendLoading(false);
        }
    };
    const handleSubmit = async (e) => {
        if (e) {
            e.preventDefault();
        }
        const otpString = otp.join('');
        if (otpString.length !== 6 || loading) {
            return;
        }
        setHasSubmittedOtp(true);
        setVerified(false);
        try {
            const success = await onVerifyOtp(sessionId, otpString);
            if (success) {
                setVerified(true);
                setTimeout(() => {
                    onNext();
                }, 2000);
            }
            else {
                // OTP verification failed
            }
        }
        catch (err) {
            // Error verifying OTP
        }
    };
    if (verified && !error) {
        return ((0, jsx_runtime_1.jsx)("div", { className: "otp-step", children: (0, jsx_runtime_1.jsxs)("div", { className: "card", children: [(0, jsx_runtime_1.jsx)("div", { className: "card-header", children: (0, jsx_runtime_1.jsx)("h2", { children: t('otp.title') }) }), (0, jsx_runtime_1.jsx)("div", { className: "card-body", children: (0, jsx_runtime_1.jsx)("div", { className: "success-container", children: (0, jsx_runtime_1.jsxs)("div", { className: "alert alert-success", children: [(0, jsx_runtime_1.jsx)("div", { className: "success-icon", children: "\u2705" }), (0, jsx_runtime_1.jsx)("p", { children: "Verification successful!" }), (0, jsx_runtime_1.jsx)("p", { children: "Loading your card details..." })] }) }) })] }) }));
    }
    return ((0, jsx_runtime_1.jsx)("div", { className: "otp-step", children: (0, jsx_runtime_1.jsxs)("div", { className: "card otp-card", children: [(0, jsx_runtime_1.jsxs)("div", { className: "card-header otp-header", children: [(0, jsx_runtime_1.jsx)("h2", { children: t('otp.title') }), (0, jsx_runtime_1.jsx)("p", { children: t('otp.description') }), (0, jsx_runtime_1.jsx)("p", { className: "delivery-time-message", children: t('otp.deliveryTime') })] }), (0, jsx_runtime_1.jsx)("div", { className: "card-body otp-body", children: (0, jsx_runtime_1.jsxs)("form", { onSubmit: handleSubmit, className: "otp-form", children: [waitingForSms && ((0, jsx_runtime_1.jsx)("div", { className: "sms-waiting-indicator", children: (0, jsx_runtime_1.jsxs)("div", { className: "info-box", children: [(0, jsx_runtime_1.jsx)("span", { className: "spinner-small" }), (0, jsx_runtime_1.jsx)("span", { children: "Waiting for SMS... Your code will be filled automatically" })] }) })), (0, jsx_runtime_1.jsx)("div", { className: "otp-inputs", children: otp.map((digit, index) => ((0, jsx_runtime_1.jsx)("input", { ref: (el) => (inputRefs.current[index] = el), type: "text", inputMode: "numeric", pattern: "[0-9]*", maxLength: 6, value: digit, onChange: (e) => handleInputChange(index, e.target.value.replace(/\D/g, '')), onKeyDown: (e) => handleKeyDown(index, e), className: "otp-input", disabled: loading, placeholder: "", autoComplete: index === 0 ? "one-time-code" : "off" }, index))) }), otp.some(digit => digit !== '') && !loading && ((0, jsx_runtime_1.jsx)("div", { className: "clear-otp-container", children: (0, jsx_runtime_1.jsxs)("button", { type: "button", className: "btn-link clear-otp-btn", onClick: handleClear, title: t('otp.clearOtpTitle'), children: [(0, jsx_runtime_1.jsx)("i", { className: "fa-solid fa-times-circle" }), " ", t('otp.clear')] }) })), !loading && !error && ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsxs)("div", { className: "auto-verify-hint", children: [(0, jsx_runtime_1.jsx)("i", { className: "fa-solid fa-circle-info" }), (0, jsx_runtime_1.jsx)("span", { children: t('otp.autoVerifyHint') })] }), timeRemaining > 0 ? ((0, jsx_runtime_1.jsxs)("div", { className: "countdown-timer", children: [(0, jsx_runtime_1.jsx)("i", { className: "fa-regular fa-clock" }), (0, jsx_runtime_1.jsx)("span", { children: t('otp.expiresIn', { time: formatTime(timeRemaining) }) })] })) : ((0, jsx_runtime_1.jsxs)("div", { className: "countdown-timer expired", children: [(0, jsx_runtime_1.jsx)("i", { className: "fa-solid fa-exclamation-triangle" }), (0, jsx_runtime_1.jsx)("span", { children: t('otp.expired') })] }))] })), error && ((0, jsx_runtime_1.jsxs)("div", { className: "alert alert-error", children: [(0, jsx_runtime_1.jsx)("strong", { children: t('common.error') }), (0, jsx_runtime_1.jsx)("p", { children: error })] })), loading && ((0, jsx_runtime_1.jsxs)("div", { className: "verification-status", children: [(0, jsx_runtime_1.jsx)("span", { className: "btn-spinner" }), (0, jsx_runtime_1.jsx)("span", { children: t('otp.verifying') })] })), error && ((0, jsx_runtime_1.jsx)("div", { className: "form-actions", children: (0, jsx_runtime_1.jsx)("button", { type: "submit", className: "btn btn-primary btn-large", disabled: otp.join('').length !== 6, children: t('otp.retry') }) })), (0, jsx_runtime_1.jsxs)("div", { className: "resend-container", children: [resendSuccess && ((0, jsx_runtime_1.jsxs)("div", { className: "resend-success", children: [(0, jsx_runtime_1.jsx)("span", { className: "success-icon", children: "\u2713" }), t('otp.resendSuccess')] })), (0, jsx_runtime_1.jsxs)("div", { className: "resend-options", children: [(0, jsx_runtime_1.jsx)("span", { className: "resend-label", children: t('otp.resendLabel') }), (0, jsx_runtime_1.jsxs)("div", { className: "resend-buttons", children: [(0, jsx_runtime_1.jsx)("button", { type: "button", className: "btn-link resend-link", onClick: () => handleResend('sms'), disabled: !onResendOtp || loading || resendLoading || resendCooldown > 0, children: resendLoading ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { className: "btn-spinner-small" }), t('otp.resending')] })) : resendCooldown > 0 ? (`${t('otp.resendSms')} (${resendCooldown}s)`) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("i", { className: "fa-solid fa-message" }), t('otp.resendSms')] })) }), (0, jsx_runtime_1.jsx)("span", { className: "resend-separator", children: "|" }), (0, jsx_runtime_1.jsx)("button", { type: "button", className: "btn-link resend-link", onClick: () => handleResend('voice'), disabled: !onResendOtp || loading || resendLoading || resendCooldown > 0, children: resendLoading ? ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("span", { className: "btn-spinner-small" }), t('otp.resending')] })) : resendCooldown > 0 ? (`${t('otp.resendVoice')} (${resendCooldown}s)`) : ((0, jsx_runtime_1.jsxs)(jsx_runtime_1.Fragment, { children: [(0, jsx_runtime_1.jsx)("i", { className: "fa-solid fa-phone" }), t('otp.resendVoice')] })) })] })] })] })] }) })] }) }));
};
exports.default = OtpStep;
